import { useCallback, useMemo, useState } from 'react';
import { Documents } from '@dealstack/frontend/shared/components/Documents/Documents';
import { useDocuments as useSelectedEntity } from './useDocuments';
import { HeaderAction } from './HeaderActions';
import { DocumentDrawer } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/DocumentDrawer';
import { EditDocument } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/types';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Flex } from '@mantine/core';
import { useRemoveDocument } from '@dealstack/frontend/shared/components/Documents/hooks/useRemoveDocument';
import { usePreviewFile } from '@dealstack/frontend/shared/components/Documents/hooks/usePreviewFile';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import {
	TableOperations,
	useMantineTableWithPaginationFilteringSorting,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { useDocuments } from '@dealstack/frontend/shared/components/Documents/hooks/useDocuments';
import {
	DocumentFieldFilter,
	DocumentLinkTarget,
	DocumentsRes,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { Row } from '@tanstack/react-table';
import { DEFAULT_DOCUMENTS_TABLE_OPERATIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/constants';
import { hasFilterApplied } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/hasFilterApplied';
import { hasSearchApplied } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/hasSearchApplied';
import { useDocumentListColumns } from '@dealstack/frontend/shared/components/Documents/hooks/useDocumentListColumns';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

export const DocumentsLayout = () => {
	const tCapManage = useTranslationCapManage();
	const tCommon = useTranslationCommonUi();
	const [isUploadActive, setIsUploadActive] = useState<boolean>(false);
	const { dealId } = useRequiredParams('dealId');
	const [hasDocuments, setHasDocuments] = useState<boolean>(false);
	const [editDocument, setEditDocument] = useState<EditDocument>({ documentId: '' });

	const { selectedEntityId, selectedEntityName } = useSelectedEntity();

	// This tab is always scoped to a person.
	const target = useMemo<DocumentLinkTarget>(
		() => ({ personId: selectedEntityId }),
		[selectedEntityId]
	);

	const [classifierFilters, setClassifierFilters] = useState<string[]>([]);

	const { downloadFile } = useDownloadFileUsingFileStorageUrl();
	const { previewFile, previewDocument, setPreviewDocument } = usePreviewFile();
	const { removeDocumentLink } = useRemoveDocument();

	const handleRemoveDocument = useCallback(
		(row: Row<DocumentsRes>) => {
			void removeDocumentLink({
				documentId: row.original.documentId,
				personId: selectedEntityId,
				dealId,
			});
		},
		[removeDocumentLink, selectedEntityId, dealId]
	);

	const columns = useDocumentListColumns({
		tCapManage,
		tCommon,
		classifierFilters,
		onRowClick: (row) =>
			setEditDocument({
				documentId: row.original.documentId,
				target,
			}),
		downloadFile: (fileId: string, dealId: string, fileName: string) =>
			downloadFile({ fileId, dealId, fileName }),
		previewFile: (document: DocumentsRes, dealId: string) => previewFile(document, dealId),
		removeDocument: handleRemoveDocument,
		dealId,
		targetId: selectedEntityId,
	});

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting(columns, DEFAULT_DOCUMENTS_TABLE_OPERATIONS);

	const hasFilterAppliedValue = hasFilterApplied(dataOperations);
	const hasSearchAppliedValue = hasSearchApplied(dataOperations);

	const { data: documentsData, isFetching: isDocumentsLoading } = useDocuments({
		dealId,
		target,
		dataOperations: dataOperations as TableOperations<DocumentFieldFilter>,
		setClassifierFilters,
		setHasDocuments,
	});

	const setUploadActive = () => {
		setIsUploadActive(true);
	};

	const setUploadInActive = () => {
		setIsUploadActive(false);
	};

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.DOCUMENTS]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityName,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityName]
	);

	useBreadcrumbs(breadcrumbs);

	return (
		<Flex direction="column" p="1rem" gap="1rem" h="100%">
			<Flex justify="flex-end">
				<HeaderAction
					hasDocuments={hasDocuments}
					isUploadActive={isUploadActive}
					setUploadActive={setUploadActive}
					setUploadInActive={setUploadInActive}
				/>
			</Flex>
			<Documents
				dealId={dealId}
				setEditDocument={setEditDocument}
				target={target}
				isUploadActive={isUploadActive}
				setIsUploadActive={setIsUploadActive}
				showFilters={true}
				columns={columns}
				mantineTableDataOperations={mantineTableDataOperations}
				documents={documentsData?.payload}
				isDocumentsLoading={isDocumentsLoading}
				hasFilterApplied={hasFilterAppliedValue}
				hasSearchApplied={hasSearchAppliedValue}
				previewDocument={previewDocument}
				setPreviewDocument={setPreviewDocument}
			/>
			<DocumentDrawer
				editDocument={editDocument}
				setEditDocument={setEditDocument}
				dataOperations={dataOperations as TableOperations<DocumentFieldFilter>}
			/>
		</Flex>
	);
};
